import { LazyImage } from '@/components/ui/LazyImage';
import type { SectionProps } from '@/types';

export function HeroSection({ business, config, theme }: SectionProps) {
  const headline = (config.headline as string) || business.name;
  const subheadline = (config.subheadline as string) || business.tagline;
  const bannerUrl =
    (config.bannerUrl as string) ||
    business.gallery[0]?.url ||
    undefined;

  const today = new Date()
    .toLocaleDateString('en-US', { weekday: 'short' })
    .toLowerCase()
    .slice(0, 3);
  const todayHours = business.hours.find((h) => h.day_of_week.startsWith(today));

  const formatTime = (iso: string | null): string => {
    if (!iso) return '';
    const match = iso.match(/(\d{2}):(\d{2})/);
    if (!match) return iso;
    const hours = parseInt(match[1], 10);
    const mins = match[2];
    const period = hours >= 12 ? 'PM' : 'AM';
    const h12 = hours % 12 || 12;
    return `${h12}:${mins} ${period}`;
  };

  return (
    <section className="relative overflow-hidden rounded-brand bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      {bannerUrl && (
        <div className="absolute inset-0">
          <LazyImage
            src={bannerUrl}
            alt={business.name}
            thumbnailSrc={business.gallery[0]?.thumbnail_url}
            aspectRatio="aspect-auto"
            className="h-full min-h-[320px] opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
        </div>
      )}

      <div className="relative container-page py-12 sm:py-16">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-2">
            {business.category && (
              <span
                className="rounded-full px-3 py-1 text-xs font-medium"
                style={{ backgroundColor: `${theme.primaryColor ?? '#2563eb'}33` }}
              >
                {business.category.name}
              </span>
            )}
            {business.is_verified && (
              <span className="flex items-center gap-1 rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-medium text-emerald-300">
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Verified
              </span>
            )}
          </div>

          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {headline}
          </h1>

          {subheadline && (
            <p className="mt-3 text-lg text-slate-300">{subheadline}</p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-1">
              <svg className="h-4 w-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              {business.rating_avg.toFixed(1)} ({business.rating_count} reviews)
            </span>
            {business.city && business.state && (
              <span>
                {business.city.name}, {business.state.name}
              </span>
            )}
            {todayHours && !todayHours.is_closed && (
              <span>
                Open today {formatTime(todayHours.open_time)} – {formatTime(todayHours.close_time)}
              </span>
            )}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {business.phone && (
              <a
                href={`tel:${business.phone}`}
                className="inline-flex items-center justify-center gap-2 rounded-brand px-6 py-3 text-base font-medium text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: theme.primaryColor ?? 'var(--brand-primary)' }}
              >
                Call now
              </a>
            )}
            {business.whatsapp && (
              <a
                href={`https://wa.me/${business.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-brand bg-emerald-600 px-6 py-3 text-base font-medium text-white transition hover:bg-emerald-700"
              >
                WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
